import { FileSpreadsheet, GripVertical, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import type { Cascade } from '../elements/fieldConfig';
import { CascadeImportDialog } from './CascadeImportDialog';

/**
 * An editable, reorderable option list — used for Drop Down, Single / Multiple Choice,
 * State / District / City, and for each dependent dropdown's own options.
 *
 * ONE COMPONENT FOR ALL OF THEM. The reference repeats this markup three times (parent options,
 * mapped options, choice options) with the drag state threaded through four extra parameters —
 * `fieldId`, `index`, `isMapped`, `mapBlockIndex` — so that one shared handler can tell the three
 * lists apart. Giving each list its own component instance means each owns its own drag state and
 * none of that plumbing exists: there is nothing to tell apart.
 */
export function FieldOptionsEditor({
  label = 'Options',
  options,
  onChange,
  onImportCascade,
}: {
  label?: string;
  options: string[];
  onChange: (options: string[]) => void;
  /**
   * Supplied only by field types that can hold DEPENDENT dropdowns (Drop Down today).
   *
   * When present, Import accepts a multi-column sheet and turns the field into a cascade; when
   * absent — Single / Multiple Choice — a multi-column sheet is refused with an explanation. Passing
   * a callback rather than a `canCascade` flag keeps this component from knowing anything about
   * cascade shape: it only forwards what the dialog produced.
   */
  onImportCascade?: (cascade: Cascade) => void;
}) {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);

  function reorder(from: number, to: number) {
    if (from === to) return;
    const next = [...options];
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return;
    next.splice(to, 0, moved);
    onChange(next);
  }

  function endDrag() {
    setDraggingIndex(null);
    setDragOverIndex(null);
  }

  return (
    <div className="flex flex-col">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="block text-[length:var(--text-14)] text-black">{label}</span>
        {/* The same dialog the cascade editor uses, in single-column mode. A list of forty branch
            names is no more typeable one input at a time than a cascade is. */}
        <button
          type="button"
          onClick={() => setImporting(true)}
          className="flex shrink-0 items-center gap-1.5 text-[length:var(--text-12)] font-semibold text-primary transition-colors hover:underline"
        >
          <FileSpreadsheet size={13} strokeWidth={2.5} />
          Import from sheet
        </button>
      </div>

      <CascadeImportDialog
        open={importing}
        // THE SHEET DECIDES, where the field allows both: one column is an option list, more than
        // one is a chain of dependent dropdowns.
        allow={onImportCascade ? 'both' : 'options'}
        existing={null}
        onCancel={() => setImporting(false)}
        onApplyOptions={(imported) => {
          onChange(imported);
          setImporting(false);
        }}
        onApplyCascade={
          onImportCascade &&
          ((cascade) => {
            onImportCascade(cascade);
            setImporting(false);
          })
        }
      />

      {options.length === 0 ? (
        <button
          type="button"
          onClick={() => onChange(['Option 1'])}
          className="flex items-center gap-1.5 self-start text-[length:var(--text-14)] font-semibold text-primary hover:underline"
        >
          <Plus size={14} strokeWidth={2.5} />
          Add the first option
        </button>
      ) : (
        <div className="grid w-full grid-cols-1 gap-4 pr-7 sm:grid-cols-2">
          {options.map((option, index) => (
            // biome-ignore lint/a11y/noStaticElementInteractions: a drag-and-drop drop target. The whole row must accept the drop (dropping on the grip alone would make reordering near-impossible on touch), and there is no element role for "reorderable row". Keyboard users are not stranded: the text input, delete and add buttons inside are all real focusable controls, and order can be changed by retyping the option text.
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: options are plain strings with no id — duplicates are legal and expected in stored data, so the text cannot identify a row. Position IS the identity here, which is also what reordering rewrites.
              key={index}
              draggable
              onDragStart={() => setDraggingIndex(index)}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverIndex !== index) setDragOverIndex(index);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (draggingIndex !== null) reorder(draggingIndex, index);
                endDrag();
              }}
              onDragEnd={endDrag}
              className={cn(
                'relative flex w-full items-center gap-2 transition-all duration-150',
                draggingIndex === index && 'scale-95 opacity-30',
                dragOverIndex === index &&
                  draggingIndex !== index &&
                  'rounded-xl ring-2 ring-primary',
              )}
            >
              <span className="shrink-0 cursor-grab rounded p-0.5 text-[#94A3B8] transition-colors hover:bg-gray-100 hover:text-[#475569] active:cursor-grabbing">
                <GripVertical size={16} />
              </span>

              <div className="flex flex-1 items-center rounded-lg border border-placeholder bg-white px-4 py-2.5 shadow-2xs transition-all focus-within:border-primary">
                <input
                  type="text"
                  value={option}
                  placeholder={`Option ${index + 1}`}
                  onChange={(e) => {
                    const next = [...options];
                    next[index] = e.target.value;
                    onChange(next);
                  }}
                  className="w-full border-none bg-transparent text-[length:var(--text-14)] text-[#0F172A] outline-none placeholder:text-[#94A3B8]"
                />
                {/* Deletable down to ONE option, not two. The reference hides delete on index 0,
                    which makes the first option permanent — so a typo in it can be edited but a
                    wrong first option cannot be removed without deleting the whole field. A list
                    of one is the real floor: zero options is a dropdown with nothing in it. */}
                {options.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Delete option ${index + 1}`}
                    onClick={() => onChange(options.filter((_, i) => i !== index))}
                    className="ml-1 shrink-0 p-1 text-destructive transition-colors hover:text-[#DC2626]"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              {index === options.length - 1 && (
                <button
                  type="button"
                  aria-label="Add option"
                  onClick={() => onChange([...options, `Option ${options.length + 1}`])}
                  className="absolute top-1/2 -right-7 z-10 flex h-4.5 w-4.5 -translate-y-1/2 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-xs transition-colors hover:bg-[#0038CC]"
                >
                  <Plus size={12} strokeWidth={2.5} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
